import React from 'react';
import { useDispatch } from 'react-redux';
import { createStore } from 'jotai';
// TODO: generalize noc drilldown in to a common trends drilldown
import { initDrilldown } from '@store/trends/tempDashboard/nocDrilldown/actions';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { dimensionPanelAtom, metricPanelAtom, summaryPanelAtom } from '@src/atoms/panels';
import { aiAlertAtom, manualAlertAtom } from '@src/atoms/toggles';
import {
  diagnosticsModeAtom,
  dodAtom,
  layoutAtom,
  logAxisAtom,
  percentileAtom,
  primaryFilterVisibilityAtom,
  sliceAreaAtom,
  unitAtom,
  widgetPanelVisibilityAtom,
  yScaleModeAtom,
  zoomAreaAtom,
} from '@components/metrics-panel-hz/atoms';
import Main from './Main';
import AppMain from '../AppExperience/index';
import TempDashboardHoc from './TempDashboardHoc';
import {
  isTableCompareEnabledAtom,
  activeTabAtom,
} from '@src/components/dimension-panel-hz/atoms/index';
import { useCaseKeyAtom, hasExplicitComparisonAtom } from '@src/atoms/pageMeta';
import ClientTempDashboardHoc from './ClientTempDashboardHoc';

const RenderPage = {
  EI: Main,
  AI: Main,
  APP: AppMain,
}[CURRENT_PRODUCT_MODE];

//TODO: combine with share flow init store
function initStore({ dashboard: state = {} }) {
  const store = createStore();

  if ('metricPanel' in state) {
    const {
      unit = '%',
      sliceArea = null,
      zoomArea = null,
      diagnosticsMode = false,
      logAxis = false,
      primaryFilterVisibility = true,
      layout = '4s',
      widgetPanelVisibility = false,
      percentile = 'avg',
      yScaleMode = false,
    }: any = state.metricPanel;
    store.set(layoutAtom, layout);
    store.set(unitAtom, unit);
    store.set(percentileAtom, percentile);
    store.set(yScaleModeAtom, yScaleMode);
    store.set(sliceAreaAtom, sliceArea);
    store.set(zoomAreaAtom, zoomArea);
    store.set(diagnosticsModeAtom, diagnosticsMode);
    store.set(logAxisAtom, logAxis);
    store.set(primaryFilterVisibilityAtom, primaryFilterVisibility);
    store.set(widgetPanelVisibilityAtom, widgetPanelVisibility);
  }

  if ('toggles' in state) {
    const { sections, diagnostics }: any = state.toggles;
    if (sections) {
      const { summary = true, metric = true, dimension = true } = sections;
      store.set(summaryPanelAtom, summary);
      store.set(metricPanelAtom, metric);
      store.set(dimensionPanelAtom, dimension);
    }

    if (diagnostics) {
      const { manualAlert = false, aiAlert = false, dod = false } = diagnostics;
      store.set(manualAlertAtom, manualAlert);
      store.set(aiAlertAtom, aiAlert);
      store.set(dodAtom, dod);
    }
  }

  if ('isTableCompareEnabled' in state) {
    const { isTableCompareEnabled = false }: any = state;
    store.set(isTableCompareEnabledAtom, isTableCompareEnabled);
  }

  // Handle dimension panel configuration
  if ('dimensionPanel' in state) {
    const dimensionPanel: any = state.dimensionPanel;
    if (dimensionPanel?.activeTab !== undefined) {
      store.set(activeTabAtom, dimensionPanel.activeTab);
    }
  }

  if ('usecaseKey' in state && !!state.usecaseKey) {
    store.set(useCaseKeyAtom, state.usecaseKey as string);
  }

  // If comparison interval is explicitly set (e.g., from Nexa DoD merged links),
  // mark it to prevent SummaryContainer from auto-calculating comparison
  if ('comparison' in state) {
    const comparison = (state as any).comparison;
    if (comparison?.interval?.from && comparison?.interval?.to) {
      store.set(hasExplicitComparisonAtom, true);
    }
  }

  return store;
}

function DrilldownDashboard(props) {
  const dispatch = useDispatch();

  const applyCallback = (payload, doneCallback) => {
    // compatible with old and new format
    dispatch(initDrilldown(payload.dashboard || payload.config));
    doneCallback();
  };

  return (
    <ClientTempDashboardHoc {...props} applyCallback={applyCallback} initStore={initStore}>
      {({ store, title }) => <RenderPage {...props} store={store} title={title} />}
    </ClientTempDashboardHoc>
  );
}

export default DrilldownDashboard;
